import {
	HTTP_INTERCEPTORS,
	HttpErrorResponse,
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { TokenModel } from '../models/TokenModel';

const TOKEN_HEADER_KEY = 'Authorization';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

	private isRefreshing$ = new BehaviorSubject<boolean>(false);

	constructor(
		private injector: Injector,
	) {
	}

	get authService() {
		return this.injector.get(AuthService);
	}

	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<Object>> {
		if (request.url.includes('mia-auth/me') || request.headers.get("skipAuthInterceptor")) {
			return next.handle(request);
		}

		const token = this.authService.getToken();
		return next.handle(this.addTokenHeader(request, token?.access_token ?? ''))
			.pipe(
				tap((res: any) => {
					if (res?.body?.success === false
						&& res?.body?.error?.code === -2
						&& res?.body?.error?.message === 'Authorization failed'
					) {
						// The token is expired
						throw new HttpErrorResponse({ status: 401, statusText: 'Unauthorized' })
					}
					return res;
				}),
				catchError(error => {
					if (error instanceof HttpErrorResponse && error.status === 401) {
						return this.handleExpiredToken(request, next);
					}
					return throwError(error); ;
				})
			);
	}

	private handleExpiredToken(request: HttpRequest<any>, next: HttpHandler) {
		if (!this.isRefreshing$.getValue()) {
			this.isRefreshing$.next(true);

			const token = this.authService.getToken();

			if (token) {
				return this.authService.refreshToken().pipe(
					switchMap((res) => {
						this.isRefreshing$.next(false);
						const newToken = new TokenModel(res.response?.access_token ?? '', res.response?.refresh_token ?? '', res.response?.expires ?? 0)

						if (res.response) {
							this.authService.saveToken(newToken);
						}
						// Retry the request with the new token
						return next.handle(this.addTokenHeader(request, newToken.access_token))
							.pipe(
								tap((request: any) => {
									if (request?.body?.success === false
										&& request?.body?.error?.code === -2
										&& request?.body?.error?.message === 'Authorization failed'
									) {
										// Invalid refresh token logout we can't do anything
										this.authService.logout();
										return throwError(() => request);
									}

									return request
								})
							)
					}),
					catchError((err) => {
						this.isRefreshing$.next(false);
						this.authService.logout();
						return throwError(() => err);
					})
				)
			}
			this.isRefreshing$.next(false);
			this.authService.logout();
			return throwError('Missing current token');
		}

		return next.handle(this.addTokenHeader(request, localStorage.getItem('refreshToken')));
	}

	private addTokenHeader(request: HttpRequest<any>, token: string | null) {
		return request.clone({ headers: request.headers.set(TOKEN_HEADER_KEY, "Bearer " + token) });
	}
}

export const authInterceptorProviders = [
	{ provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true }
];
